import React, { useCallback, useMemo, useState } from "react";
import submit from "submit-form";
import PropTypes from "prop-types";
import Modal from "../../../../parts/modal-new";
import {
  getPushConversation,
  modifyConversation,
} from "../../../../utils/chat-util";
import { ParticipantCard } from "../components/ParticipantCard";

export const EditChannelModal = ({ locals, api }) => {
  const {
    flash,
    activeSubscribersList = [],
    activeUser = {},
    activeUserToRequest = {},
    query,
    usersOrganization = {
      organizationActiveUsers: [],
    },
    allCommunityConversations = {
      readChannels: [],
      readDirects: [],
      unreadChats: [],
    },
  } = locals;
  const { organizationActiveUsers = [] } = usersOrganization || {};

  const { webAppTheme = "dark" } = activeUser;
  const {
    _id: activeUserId,
    avatar: activeUserAvatar,
    name: activeUserName,
  } = activeUserToRequest;
  const { conversationId } = query;
  //we are finding the conversation selected in the conversations array
  const conversationsArray = [
    ...allCommunityConversations.readChannels,
    ...allCommunityConversations.readDirects,
    ...allCommunityConversations.unreadChats,
  ];
  const conversation = findConversationById(conversationsArray, conversationId);
  const [conversationState, setConversationState] = useState(conversation);
  const [isLoading, setIsLoading] = useState(false);
  //we are extracting the properties of the conversation
  const {
    subject,
    custom = {
      blocked: "[]",
      guests: "[]",
    },
    participants = {},
  } = conversationState;
  const [subjectState, setSubjectState] = useState(subject);
  const { blocked = "[]", guests = "[]" } = custom;
  //-------------Init of building the participants array------------------
  const blockedList = new Set(JSON.parse(blocked));
  const guestsList = new Set(JSON.parse(guests));
  const participantIds = new Set(Object.keys(participants));
  //We are filtering the members of the organization
  const subscriberIdsSet = new Set(activeSubscribersList.map(({ _id }) => _id));
  const updatedParticipantsList = useMemo(() => {
    return Array.from(participantIds).reduce((acc, participantId) => {
      if (subscriberIdsSet.has(participantId)) {
        return acc; // We are ignoring if the participant is a subscriber, we only want coaches and org members
      }
      //in case the user isn't an organization organizationUser will be undefined, so the coach will be the owner
      const organizationUser = organizationActiveUsers.find(
        ({ _id }) => _id === participantId
      );

      acc.push({
        name: organizationUser ? organizationUser.name : activeUserName,
        _id: organizationUser ? organizationUser._id : activeUserId,
        isBlocked: false,
        isGuest: false,
        isRegistered: true,
        role: organizationUser
          ? organizationUser._id === activeUserId
            ? "Owner"
            : "Organization Member"
          : "Owner",
        avatar: organizationUser ? organizationUser.avatar : activeUserAvatar,
      });

      return acc;
    }, []);
  }, [
    participantIds,
    activeSubscribersList,
    organizationActiveUsers,
    activeUserId,
    activeUserName,
    activeUserAvatar,
  ]);

  //We are filtering the subscribers
  const updatedSubscribersList = activeSubscribersList.reduce(
    (acc, subscriber) => {
      const { _id: subscriberId, setup } = subscriber;
      const isGuest =
        guestsList.has(subscriberId) && !participantIds.has(subscriberId);
      const isParticipant = participantIds.has(subscriberId);
      // We are excluding the subscribers if they aren't in the guestsList or participantIds
      if (isGuest || isParticipant) {
        const isBlocked = blockedList.has(subscriberId);
        acc.push({
          ...subscriber,
          isBlocked,
          isGuest,
          isRegistered: setup === "finished",
          role: "Subscriber",
        });
      }

      return acc;
    },
    []
  );

  //We are combining the two arrays
  const combinedList = useMemo(() => {
    const combinedParticipantsMap = new Map();
    updatedParticipantsList.forEach((participant) => {
      combinedParticipantsMap.set(participant._id, participant);
    });
    updatedSubscribersList.forEach((subscriber) => {
      if (!combinedParticipantsMap.has(subscriber._id)) {
        combinedParticipantsMap.set(subscriber._id, subscriber);
      }
    });
    return Array.from(combinedParticipantsMap.values());
  }, [updatedParticipantsList, updatedSubscribersList]);

  //-------------End of building the participants array------------------

  /**
   * To know if the button is enable or disable
   * @returns
   */
  const isButtonDisabled = () => {
    return subjectState.length === 0 || subjectState === subject;
  };

  /**
   * Handle the block/unblock make/revoke admin
   * @param {*} option
   * @param {*} participantId
   */
  const handleParticipantAction = useCallback(
    (option, participantId) => {
      const body = {
        option,
        conversationId,
        custom,
        participantId,
      };
      handleModifyConversation(body);
    },
    [conversationId, custom]
  );

  /**
   * Change the name of the channel
   */
  const handleChangeSubject = () => {
    const filteredSubjects = conversationsArray
      .filter((item) => item.subject)
      .map((item) => item.subject);

    const isSubjectInArray = filteredSubjects.includes(subjectState);

    if (isSubjectInArray) {
      flash("error", "This channel name already exists");
      return submit(`/actions/community-chat/refresh`, {
        method: "POST",
      });
    } else {
      const body = {
        option: "edit-subject",
        conversationId,
        subject: subjectState,
      };

      handleModifyConversation(body);
    }
  };

  /**
   * API request to modify a channel and then do the GET request to update the state (if we are blocking/unblocking/make admin/revoke admin)
   * @param {*} body
   */
  const handleModifyConversation = useCallback(
    async (body) => {
      const { option } = body;
      if (option === "edit-subject") {
        submit(`/actions/community-chat/modify-conversation`, {
          method: "POST",
          body,
        });
      } else {
        try {
          setIsLoading(true);
          await modifyConversation(api, body);
          const updatedConversation = await getPushConversation(
            api,
            conversationId
          );
          setConversationState(updatedConversation);

          flash("success", "The conversation has been updated successfully.");
        } catch (e) {
          flash("error", e.message);
        } finally {
          setIsLoading(false);
        }
        submit(`/actions/community-chat/refresh`, {
          method: "POST",
        });
      }
    },
    [api, activeUserId, conversationId, flash]
  );

  return (
    <Modal
      className="edit-channel-modal"
      title={"Edit channel"}
      toClear={["conversationId"]}
    >
      <div className="body">
        <label htmlFor="channel-name">Channel Name</label>
        <input
          id="channel-name"
          name="name"
          className="enter-channel-name"
          placeholder="Enter Channel Name"
          value={subjectState}
          onChange={({ target: { value } }) => setSubjectState(value)}
        />
        <div>
          <span>Subscribers: {combinedList.length}</span>
          <div className="edit-channel-cards-container">
            {combinedList.map((item) => {
              return (
                <ParticipantCard
                  key={item._id}
                  participant={item}
                  handleParticipantAction={handleParticipantAction}
                  webAppTheme={webAppTheme}
                />
              );
            })}
          </div>
          {isLoading && (
            <div className="spinner-div">
              <img
                src="/img/spinner.gif"
                width="50"
                height="50"
                alt="spinner"
              />
            </div>
          )}
        </div>
      </div>
      <footer>
        <button
          data-button-type="primary"
          disabled={isButtonDisabled()}
          onClick={handleChangeSubject}
        >
          Save
        </button>
      </footer>
    </Modal>
  );
};

EditChannelModal.propTypes = {
  locals: PropTypes.object,
  api: PropTypes.any,
};

/**
 * Function to find the current conversation
 * @param {*} conversationsArray
 * @param {*} conversationId
 * @returns
 */
const findConversationById = (conversationsArray, conversationId) => {
  return (
    conversationsArray.find(({ id }) => id === conversationId) || {
      subject: "",
      custom: {
        blocked: "[]",
        guests: "[]",
      },
      participants: {},
    }
  );
};
